import AsyncStorage from "@react-native-async-storage/async-storage";

interface CrashLog {
  timestamp: number;
  error: string;
  stackTrace?: string;
  userAgent?: string;
  appVersion?: string;
  userId?: string;
  route?: string;
  additionalInfo?: any;
}

class CrashReporter {
  private static instance: CrashReporter;
  private readonly maxLogs = 50; // Keep only the latest 50 crash logs
  private readonly storageKey = "@crash_logs";

  static getInstance(): CrashReporter {
    if (!CrashReporter.instance) {
      CrashReporter.instance = new CrashReporter();
    }
    return CrashReporter.instance;
  }

  // Log a crash with relevant information
  async logCrash(
    error: Error | string,
    additionalInfo?: any,
    route?: string,
    userId?: string
  ): Promise<void> {
    try {
      const crashLog: CrashLog = {
        timestamp: Date.now(),
        error: typeof error === "string" ? error : error.message,
        stackTrace: typeof error === "object" ? error.stack : undefined,
        route: route,
        userId: userId,
        additionalInfo: additionalInfo,
        appVersion: "1.0.0", // You can get this from your app.json or package.json
      };

      // Get existing logs
      const existingLogs = await this.getCrashLogs();

      // Add new log and keep only the latest ones
      const updatedLogs = [crashLog, ...existingLogs].slice(0, this.maxLogs);

      // Save back to storage
      await AsyncStorage.setItem(this.storageKey, JSON.stringify(updatedLogs));

      // In a production app, you might want to send this to a remote logging service
      // await this.sendToRemoteService(crashLog);
    } catch (storageError) {
      // silently ignore
    }
  }

  // Get all stored crash logs
  async getCrashLogs(): Promise<CrashLog[]> {
    try {
      const logsString = await AsyncStorage.getItem(this.storageKey);
      return logsString ? JSON.parse(logsString) : [];
    } catch (error) {
      return [];
    }
  }

  // Clear all crash logs (useful for testing or privacy)
  async clearCrashLogs(): Promise<void> {
    try {
      await AsyncStorage.removeItem(this.storageKey);
    } catch (error) {
      // silently ignore
    }
  }

  // Get crash logs formatted for sharing/debugging
  async getFormattedLogs(): Promise<string> {
    const logs = await this.getCrashLogs();

    if (logs.length === 0) {
      return "No crash logs available.";
    }

    return logs
      .map((log, index) => {
        const date = new Date(log.timestamp).toLocaleString();
        return `
Crash ${index + 1} - ${date}
Error: ${log.error}
Route: ${log.route || "Unknown"}
User ID: ${log.userId || "Not logged in"}
${log.stackTrace ? `Stack Trace: ${log.stackTrace}` : ""}
${log.additionalInfo ? `Additional Info: ${JSON.stringify(log.additionalInfo, null, 2)}` : ""}
${"=".repeat(50)}`;
      })
      .join("\n");
  }

  // Optional: Send crash logs to a remote service
  private async sendToRemoteService(crashLog: CrashLog): Promise<void> {
    // Implement your remote logging service here
    // Examples: Sentry, Crashlytics, LogRocket, etc.
    // Example implementation:
    /*
    try {
      await fetch('https://your-logging-service.com/api/crash-logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(crashLog),
      });
    } catch (error) {
      // Failed to send crash log to remote service
    }
    */
  }

  // Helper method to get basic app info
  async getAppInfo(): Promise<{ version: string; platform: string }> {
    return {
      version: "1.0.0", // Get from app.json or expo-constants
      platform: "mobile", // You can get this from Platform.OS
    };
  }
}

// Export singleton instance
export const crashReporter = CrashReporter.getInstance();

// Global error handler helper
export const setupGlobalErrorHandling = () => {
  // Handle unhandled promise rejections for React Native
  if (typeof global !== "undefined") {
    try {
      // React Native specific error handling
      const ErrorUtils = (global as any).ErrorUtils;
      if (ErrorUtils && typeof ErrorUtils.setGlobalHandler === "function") {
        const originalHandler = ErrorUtils.getGlobalHandler?.();

        ErrorUtils.setGlobalHandler((error: Error, isFatal?: boolean) => {
          crashReporter.logCrash(
            error,
            { isFatal, type: "GlobalHandler" },
            "Global",
            undefined
          );

          // Call the original handler if it exists
          if (originalHandler && typeof originalHandler === "function") {
            originalHandler(error, isFatal);
          }
        });
      }
    } catch (setupError) {
      // silently ignore
    }
  }

  // Handle unhandled promise rejections
  if (typeof global !== "undefined" && global.addEventListener) {
    try {
      global.addEventListener("unhandledrejection", (event: any) => {
        crashReporter.logCrash(
          event.reason instanceof Error ? event.reason : String(event.reason),
          { type: "UnhandledPromiseRejection" },
          "Global",
          undefined
        );
      });
    } catch (setupError) {
      // silently ignore
    }
  }
};

export default CrashReporter;
